import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export function ShellBreadcrumb({ root }: { root: "admin" | "merchant" }) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const segments = pathname.split("/").filter(Boolean);
  // segments[0] === root
  const rest = segments.slice(1);

  const rootLabel = t(`shellBreadcrumb.${root}`, { defaultValue: root.charAt(0).toUpperCase() + root.slice(1) });

  const prettify = (seg: string) => {
    // Numeric / hash-like ids: show as #id instead of a translation key
    if (/^\d+$/.test(seg)) return `#${seg}`;
    if (/^[0-9a-f-]{6,}$/i.test(seg)) return `#${seg.slice(0, 6)}`;

    const key = `shellBreadcrumb.segments.${seg}`;
    const translated = t(key, { defaultValue: "" });
    if (translated && translated !== key) return translated;
    return seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <Breadcrumb className="hidden sm:block">
      <BreadcrumbList>
        <BreadcrumbItem>
          {rest.length === 0 ? (
            <BreadcrumbPage>{rootLabel}</BreadcrumbPage>
          ) : (
            <BreadcrumbLink asChild>
              <Link to={`/${root}` as string}>{rootLabel}</Link>
            </BreadcrumbLink>
          )}
        </BreadcrumbItem>
        {rest.map((seg, i) => {
          const isLast = i === rest.length - 1;
          const href = "/" + segments.slice(0, i + 2).join("/");
          return (
            <span key={href} className="flex items-center gap-1.5">
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{prettify(seg)}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={href as string}>{prettify(seg)}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </span>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
